import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";

export const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
});

export const auth = getAuth(app);

// The 2020 code called firebase.analytics() at module scope, which ran on
// import in every environment including tests. Initialise lazily instead, only
// in production, and only where the browser actually supports it.
// Imported dynamically as well as initialised lazily: a static import puts the
// analytics SDK in the entry chunk for every visitor, including the ones who
// never reach a screen that reports anything.
let analyticsReady: Promise<unknown | null> | null = null;

function getAnalyticsInstance() {
  if (import.meta.env.DEV) return Promise.resolve(null);
  analyticsReady ??= import("firebase/analytics").then(async ({ getAnalytics, isSupported }) =>
    (await isSupported()) ? getAnalytics(app) : null,
  );
  return analyticsReady;
}

/**
 * The 2020 code sent `{ screen_name }`, which GA4 does not recognise — the
 * reserved `screen_view` event takes `firebase_screen` / `firebase_screen_class`,
 * so those screen views were silently dropped.
 */
export async function logScreenView(screenName: string): Promise<void> {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;
  const { logEvent } = await import("firebase/analytics");
  logEvent(analytics as never, "screen_view", {
    firebase_screen: screenName,
    firebase_screen_class: screenName,
  });
}
